import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  FileSpreadsheet,
  Download,
  FileText,
  CheckCircle2,
  AlertCircle,
  Info,
  Package,
  Users,
  MapPin,
  FolderTree,
  ArrowRight,
  Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadFile } from '../lib/utils';

const TemplatesPage = () => {
  const { api } = useAuth();
  const [downloading, setDownloading] = useState(null);

  // Download template from backend or generate locally
  const handleDownloadTemplate = async (templateType) => {
    setDownloading(templateType);
    try {
      if (templateType === 'units') {
        // Download empty template with examples
        const response = await api().get('/equipment/template/excel', { responseType: 'blob' });
        downloadFile(response.data, `Plantilla_Importacion_Unidades.xlsx`);
        toast.success('Plantilla descargada - elimina los ejemplos y agrega tus datos');
      } else {
        // Generate empty template locally
        const templates = {
          categories: generateCategoriesTemplate(),
          locations: generateLocationsTemplate(),
          responsibles: generateResponsiblesTemplate()
        };
        if (templates[templateType]) {
          downloadFile(templates[templateType], `Plantilla_${templateType}_${new Date().toISOString().split('T')[0]}.xlsx`);
          toast.success('Plantilla descargada');
        }
      }
    } catch (error) {
      toast.error('Error al descargar plantilla');
    } finally {
      setDownloading(null);
    }
  };

  // Template column definitions
  const unitsColumns = [
    { name: 'ID_Unidad', required: false, description: 'Identificador único de la unidad. Déjalo vacío para auto-generar.' },
    { name: 'ID_Equipo', required: false, description: 'ID del tipo de equipo. Déjalo vacío si llenas Nombre_Equipo.' },
    { name: 'Nombre_Equipo', required: true, description: 'Nombre del tipo de equipo (ej: "MacBook Pro 14"). Si no existe, se crea automáticamente.' },
    { name: 'Categoría', required: false, description: 'Categoría del equipo (ej: "Laptops"). Se crea si no existe. Por defecto: "General".' },
    { name: 'Número_Serie', required: true, description: 'Número de serie único de esta unidad. No puede repetirse.' },
    { name: 'Ubicación', required: true, description: 'Nombre de la ubicación (ej: "Almacén"). Se crea si no existe.' },
    { name: 'Estado', required: false, description: 'Estado de la unidad. Por defecto: "disponible".' },
    { name: 'Asignado_A_Movimiento', required: false, description: 'ID del movimiento si está asignada. Generalmente vacío.' },
    { name: 'Notas_Unidad', required: false, description: 'Notas específicas de esta unidad. Si contiene "Receptor #N" o similar, se extrae automáticamente como número de equipo.' },
    { name: 'Número_Equipo', required: false, description: 'Identificador de la unidad dentro de su tipo (ej: "Receptor #1", "Laptop #3"). Si está vacío, se intenta extraer de Notas_Unidad.' },
    { name: 'Notas_Equipo', required: false, description: 'Notas del tipo de equipo (ignorado en importación).' },
    { name: 'Fecha_Creación', required: false, description: 'Fecha de creación (ignorado, se genera automáticamente).' },
    { name: 'Última_Actualización', required: false, description: 'Última actualización (ignorado, se genera automáticamente).' },
  ];

  const statusOptions = [
    { value: 'disponible', description: 'Equipo disponible para asignar' },
    { value: 'asignado', description: 'Equipo asignado a un evento/movimiento' },
    { value: 'en_prestamo', description: 'Equipo prestado a un responsable' },
    { value: 'mantenimiento', description: 'Equipo en mantenimiento/reparación' },
    { value: 'falla', description: 'Equipo con fallas reportadas' },
    { value: 'perdido', description: 'Equipo extraviado o perdido' },
    { value: 'baja', description: 'Equipo dado de baja del inventario' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Plantillas de Importación</h1>
          <p className="text-muted-foreground mt-1">
            Descarga plantillas y guías para importar datos correctamente
          </p>
        </div>
      </div>

      {/* Quick Start Card */}
      <Card className="border-primary/50 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Lightbulb className="h-5 w-5 text-primary" />
            Inicio Rápido
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">1</div>
              <div>
                <p className="font-medium">Descarga la plantilla</p>
                <p className="text-sm text-muted-foreground">Usa el botón de exportar en Inventario o descarga aquí</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">2</div>
              <div>
                <p className="font-medium">Agrega tus datos</p>
                <p className="text-sm text-muted-foreground">Llena las columnas requeridas, deja IDs vacíos</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">3</div>
              <div>
                <p className="font-medium">Importa el archivo</p>
                <p className="text-sm text-muted-foreground">Ve a Inventario → Importar y selecciona tu archivo</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="units" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-flex">
          <TabsTrigger value="units" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Unidades de Equipo
          </TabsTrigger>
          <TabsTrigger value="guide" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Guía Completa
          </TabsTrigger>
        </TabsList>

        {/* Units Template Tab */}
        <TabsContent value="units" className="space-y-6">
          {/* Download Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="h-5 w-5 text-emerald-500" />
                    Plantilla de Unidades de Equipo
                  </CardTitle>
                  <CardDescription>
                    Para agregar múltiples unidades a equipos existentes o crear nuevos tipos de equipo
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => handleDownloadTemplate('units')}
                  disabled={downloading === 'units'}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {downloading === 'units' ? 'Descargando...' : 'Descargar Plantilla'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border p-4 bg-muted/30">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Info className="h-4 w-4 text-blue-500" />
                  ¿Qué incluye esta plantilla?
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Todas las unidades actuales del inventario como referencia</li>
                  <li>• Tipos de equipo sin unidades (para que veas qué equipos necesitan unidades)</li>
                  <li>• Hoja de resumen con totales por tipo de equipo</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Columns Reference */}
          <Card>
            <CardHeader>
              <CardTitle>Referencia de Columnas</CardTitle>
              <CardDescription>
                Descripción detallada de cada columna del archivo Excel
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Columna</TableHead>
                    <TableHead className="w-[100px]">Requerido</TableHead>
                    <TableHead>Descripción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unitsColumns.map((col) => (
                    <TableRow key={col.name}>
                      <TableCell className="font-mono text-sm">{col.name}</TableCell>
                      <TableCell>
                        {col.required ? (
                          <Badge className="bg-red-500/10 text-red-500 hover:bg-red-500/20">Requerido</Badge>
                        ) : (
                          <Badge variant="outline">Opcional</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{col.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Status Values */}
          <Card>
            <CardHeader>
              <CardTitle>Valores de Estado</CardTitle>
              <CardDescription>
                Valores permitidos para la columna "Estado"
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 md:grid-cols-2">
                {statusOptions.map((status) => (
                  <div key={status.value} className="flex items-center gap-3 p-3 rounded-lg border">
                    <code className="text-sm bg-muted px-2 py-1 rounded">{status.value}</code>
                    <span className="text-sm text-muted-foreground">{status.description}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Example */}
          <Card>
            <CardHeader>
              <CardTitle>Ejemplo Práctico</CardTitle>
              <CardDescription>
                Cómo agregar 3 MacBook Pro al inventario existente
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border p-2 text-left">ID_Unidad</th>
                      <th className="border p-2 text-left">ID_Equipo</th>
                      <th className="border p-2 text-left">Nombre_Equipo</th>
                      <th className="border p-2 text-left">Categoría</th>
                      <th className="border p-2 text-left">Número_Serie</th>
                      <th className="border p-2 text-left">Ubicación</th>
                      <th className="border p-2 text-left">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-2 text-muted-foreground italic">(vacío)</td>
                      <td className="border p-2 text-muted-foreground italic">(vacío)</td>
                      <td className="border p-2 font-medium">MacBook Pro 14</td>
                      <td className="border p-2">Laptops</td>
                      <td className="border p-2 font-mono">SN-MAC-001</td>
                      <td className="border p-2">Almacén</td>
                      <td className="border p-2">disponible</td>
                    </tr>
                    <tr>
                      <td className="border p-2 text-muted-foreground italic">(vacío)</td>
                      <td className="border p-2 text-muted-foreground italic">(vacío)</td>
                      <td className="border p-2 font-medium">MacBook Pro 14</td>
                      <td className="border p-2">Laptops</td>
                      <td className="border p-2 font-mono">SN-MAC-002</td>
                      <td className="border p-2">Oficina Principal</td>
                      <td className="border p-2">disponible</td>
                    </tr>
                    <tr>
                      <td className="border p-2 text-muted-foreground italic">(vacío)</td>
                      <td className="border p-2 text-muted-foreground italic">(vacío)</td>
                      <td className="border p-2 font-medium">MacBook Pro 14</td>
                      <td className="border p-2">Laptops</td>
                      <td className="border p-2 font-mono">SN-MAC-003</td>
                      <td className="border p-2">Sala de Juntas</td>
                      <td className="border p-2">disponible</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-4 p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                <p className="text-sm flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span>
                    <strong>Resultado:</strong> Se crearán 3 nuevas unidades y se asignarán automáticamente al equipo "MacBook Pro 14" existente. 
                    Si el equipo no existe, se creará junto con la categoría "Laptops".
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Complete Guide Tab */}
        <TabsContent value="guide" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Guía Completa de Importación</CardTitle>
              <CardDescription>
                Todo lo que necesitas saber para importar equipos correctamente
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="step1">
                  <AccordionTrigger>
                    <span className="flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs">1</span>
                      Preparar tu archivo Excel
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <p>Tienes dos opciones para preparar tu archivo:</p>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <ArrowRight className="h-4 w-4 text-primary" />
                          Opción A: Usar plantilla exportada
                        </h4>
                        <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                          <li>Ve a Inventario</li>
                          <li>Haz clic en "Exportar"</li>
                          <li>Abre el archivo descargado</li>
                          <li>Agrega nuevas filas con tus datos</li>
                          <li>Guarda el archivo</li>
                        </ol>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <ArrowRight className="h-4 w-4 text-primary" />
                          Opción B: Crear desde cero
                        </h4>
                        <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                          <li>Descarga la plantilla de esta página</li>
                          <li>Elimina las filas de ejemplo</li>
                          <li>Agrega tus datos</li>
                          <li>Asegúrate de tener las columnas correctas</li>
                          <li>Guarda como .xlsx</li>
                        </ol>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step2">
                  <AccordionTrigger>
                    <span className="flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs">2</span>
                      Llenar los datos correctamente
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <div className="space-y-4">
                      <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                        <h4 className="font-medium mb-2 flex items-center gap-2 text-emerald-700">
                          <CheckCircle2 className="h-4 w-4" />
                          Campos que SÍ debes llenar
                        </h4>
                        <ul className="text-sm space-y-1">
                          <li><strong>Nombre_Equipo:</strong> El nombre exacto del tipo de equipo</li>
                          <li><strong>Número_Serie:</strong> Serial único de cada unidad</li>
                          <li><strong>Ubicación:</strong> Dónde estará físicamente el equipo</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
                        <h4 className="font-medium mb-2 flex items-center gap-2 text-amber-700">
                          <AlertCircle className="h-4 w-4" />
                          Campos que puedes dejar vacíos
                        </h4>
                        <ul className="text-sm space-y-1">
                          <li><strong>ID_Unidad:</strong> Se genera automáticamente</li>
                          <li><strong>ID_Equipo:</strong> Se busca por Nombre_Equipo</li>
                          <li><strong>Estado:</strong> Por defecto será "disponible"</li>
                          <li><strong>Categoría:</strong> Por defecto será "General"</li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step3">
                  <AccordionTrigger>
                    <span className="flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs">3</span>
                      Importar el archivo
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <ol className="space-y-3">
                      <li className="flex items-start gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-sm">1</span>
                        <span>Ve a la sección <strong>Inventario</strong> en el menú lateral</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-sm">2</span>
                        <span>Haz clic en el botón <strong>"Importar"</strong> en la parte superior</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-sm">3</span>
                        <span>Selecciona tu archivo Excel (.xlsx)</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-sm">4</span>
                        <span>Espera la confirmación del sistema</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-sm">5</span>
                        <span>Verifica que las unidades se hayan agregado correctamente</span>
                      </li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step4">
                  <AccordionTrigger>
                    <span className="flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs">4</span>
                      Solución de problemas comunes
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="p-3 border rounded-lg">
                        <p className="font-medium text-red-500">Error: "Número_Serie es requerido"</p>
                        <p className="text-sm text-muted-foreground">Cada unidad debe tener un número de serie único. Asegúrate de que no haya celdas vacías en esa columna.</p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <p className="font-medium text-red-500">Error: "Serial duplicado"</p>
                        <p className="text-sm text-muted-foreground">El número de serie ya existe en el sistema. Verifica que no estés importando unidades que ya fueron registradas.</p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <p className="font-medium text-amber-500">Las unidades no aparecen en el equipo correcto</p>
                        <p className="text-sm text-muted-foreground">Verifica que el "Nombre_Equipo" coincida exactamente con el nombre registrado en el sistema (incluyendo mayúsculas y acentos).</p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <p className="font-medium text-amber-500">Se creó un nuevo tipo de equipo en vez de usar el existente</p>
                        <p className="text-sm text-muted-foreground">El nombre no coincidió exactamente. Exporta primero para ver los nombres exactos de los equipos existentes.</p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="tips">
                  <AccordionTrigger>
                    <span className="flex items-center gap-2">
                      <Lightbulb className="h-5 w-5 text-amber-500" />
                      Consejos y mejores prácticas
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-1 shrink-0" />
                        <span><strong>Exporta antes de importar:</strong> Así tendrás los nombres exactos de equipos, categorías y ubicaciones.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-1 shrink-0" />
                        <span><strong>Usa números de serie únicos:</strong> Cada unidad debe tener un serial diferente, incluso si son del mismo modelo.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-1 shrink-0" />
                        <span><strong>Importa por lotes pequeños:</strong> Si tienes muchas unidades, importa de 100 en 100 para identificar errores más fácilmente.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-1 shrink-0" />
                        <span><strong>Haz respaldo primero:</strong> Antes de importaciones grandes, exporta tu inventario actual como respaldo.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-1 shrink-0" />
                        <span><strong>Revisa la hoja "Resumen_Equipos":</strong> Te muestra un resumen de unidades por tipo de equipo después de exportar.</span>
                      </li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Helper functions for generating templates (these would create Excel files)
const generateCategoriesTemplate = () => {
  // This would use a library like xlsx to generate an Excel file
  // For now, return null as we're using the export endpoint
  return null;
};

const generateLocationsTemplate = () => {
  return null;
};

const generateResponsiblesTemplate = () => {
  return null;
};

export default TemplatesPage;
